<div class="privacy-container">
  <mat-card appearance="raised" class="privacy-card">
    <mat-card-header>
      <mat-card-title>Privacy Policy</mat-card-title>
      <mat-card-subtitle>
        <p>Last updated: September 3, 2026</p>
        <p>This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your
          information when You use the Service and tells You about Your privacy rights and how the law protects You.</p>
      </mat-card-subtitle>
    </mat-card-header>
    <mat-card-content class="privacy-content">
      <h1>Interpretation and Definitions</h1>
      <h2>Interpretation</h2>
      <p>The words of which the initial letter is capitalized have meanings defined under the following conditions. The
        following definitions shall have the same meaning regardless of whether they appear in singular or in
        plural.</p>
      <h2>Definitions</h2>
      <p>For the purposes of this Privacy Policy:</p>
      <ul>
        <li>
          <p><strong>Account / Organization</strong> means a unique account / organization created for You to access our
            Service or parts of our Service.</p>
        </li>
        <li>
          <p><strong>Application</strong> refers to InvFlow Go, the software program provided by the Company and
            downloaded by You on any electronic Device.</p>
        </li>
        <li>
          <p><strong>Business Contact Data</strong> means personal information about Your own customers, suppliers,
            employees and other third parties that You or Your users enter into, upload to, or generate within the
            Service.</p>
        </li>
        <li>
          <p><strong>Company</strong> (referred to as either &quot;the Company&quot;, &quot;We&quot;, &quot;Us&quot; or
            &quot;Our&quot; in this Privacy Policy) refers to Loreggian Pty Ltd. ABN 58667862080. Level 16, 1 Denison St,
            North Sydney, NSW, 2060, Australia</p>
        </li>
        <li>
          <p><strong>Cookies</strong> are small files that are placed on Your Device by a website, containing the
            details of Your browsing history on that website among its many uses.</p>
        </li>
        <li>
          <p><strong>Country</strong> refers to: Australia</p>
        </li>
        <li>
          <p><strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a
            digital tablet.</p>
        </li>
        <li>
          <p><strong>Personal Data</strong> is any information that relates to an identified or identifiable
            individual.</p>
        </li>
        <li>
          <p><strong>Service</strong> refers to the Website and the Application.</p>
        </li>
        <li>
          <p><strong>Service Provider</strong> means any natural or legal person who processes the data on behalf of the
            Company. It refers to third-party companies or individuals employed by the Company to facilitate the
            Service, to provide the Service on behalf of the Company, to perform services related to the Service or to
            assist the Company in analysing how the Service is used.</p>
        </li>
        <li>
          <p><strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the
            Service or from the Service infrastructure itself (for example, the duration of a page visit).</p>
        </li>
        <li>
          <p><strong>Website</strong> refers to Invflow, accessible from <a href="https://app.invflow.com"
                                                                           rel="external nofollow noopener"
                                                                           target="_blank">https://app.invflow.com</a>
          </p>
        </li>
        <li>
          <p><strong>You</strong> means the individual accessing or using the Service, or the company, or other legal
            entity on behalf of which such individual is accessing or using the Service, as applicable.</p>
        </li>
      </ul>

      <h1>Our Two Roles</h1>
      <p>The Service is a business tool. The information handled through it falls into two categories, and Our
        responsibilities differ for each.</p>
      <ul>
        <li>
          <p><strong>Information We collect about You directly</strong> &mdash; such as the details You provide when
            registering an Account, Your billing information, and the Usage Data described below. For this information
            We act as the controller and this Privacy Policy governs how We handle it.</p>
        </li>
        <li>
          <p><strong>Business Contact Data You put into the Service</strong> &mdash; such as the customer, supplier and
            employee records, invoices, quotes, jobs and attachments You create. For this information We act as a
            processor on Your behalf. You decide what is collected, why it is collected and how long it is kept. We
            process it only to provide the Service to You, to comply with the law, and on Your instructions. You are
            responsible for having a lawful basis to collect that information and for providing any notices or
            obtaining any consents that the law requires of You.</p>
        </li>
      </ul>

      <h1>Collecting and Using Your Personal Data</h1>
      <h2>Types of Data Collected</h2>
      <h3>Personal Data</h3>
      <p>While using Our Service, We may ask You to provide Us with certain personally identifiable information that
        can be used to contact or identify You. Personally identifiable information may include, but is not limited
        to:</p>
      <ul>
        <li>
          <p>Email address</p>
        </li>
        <li>
          <p>First name and last name, and username</p>
        </li>
        <li>
          <p>Phone number</p>
        </li>
        <li>
          <p>Address, State, Province, ZIP/Postal code, City</p>
        </li>
        <li>
          <p>Business name, Australian Business Number (ABN) or equivalent tax registration number</p>
        </li>
        <li>
          <p>Usage Data</p>
        </li>
      </ul>
      <p>We store passwords only in hashed form. We never store Your password in a form that We can read.</p>

      <h3>Usage Data</h3>
      <p>Usage Data is collected automatically when using the Service.</p>
      <p>Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser
        type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent
        on those pages, unique device identifiers and other diagnostic data.</p>
      <p>When You access the Service by or through a mobile device, We may collect certain information automatically,
        including, but not limited to, the type of mobile device You use, Your mobile device unique ID, the IP address
        of Your mobile device, Your mobile operating system, the type of mobile Internet browser You use, unique device
        identifiers and other diagnostic data.</p>
      <p>We also maintain audit and activity logs recording actions taken within Your Organization &mdash; for example
        which user created, changed or deleted a record, and when. These logs exist to keep Your Account secure, to
        support You when something goes wrong, and to meet Our record-keeping obligations.</p>

      <h3>Information Collected while Using the Application</h3>
      <p>While using Our Application, in order to provide features of Our Application, We may collect, with Your prior
        permission:</p>
      <ul>
        <li>
          <p>Pictures and other information from Your Device's camera and photo library, and audio captured by Your
            Device's microphone while recording video</p>
        </li>
      </ul>
      <p>We use this information to allow You to attach photographs and video to a job, quote, invoice or other record.
        This information is uploaded to the Company's servers and stored against the relevant record in Your
        Organization. You can enable or disable access to this information at any time through Your Device
        settings.</p>
      <p>Where You choose to unlock the Application using Face ID, Touch ID or an equivalent fingerprint or facial
        recognition feature, that check is performed entirely by Your Device. Your biometric data never leaves Your
        Device and is never transmitted to or stored by Us.</p>

      <h3>Payment Information</h3>
      <p>Subscription payments and invoice payments taken through the Service are processed by Stripe. Payment card
        details are submitted directly to Stripe and are never received, stored or processed on Our servers. We retain
        only the information We need to administer Your subscription and reconcile payments, such as the payment status,
        the amount, the last four digits and brand of the card, and the identifiers Stripe assigns to the customer and
        the transaction. Stripe's handling of Your information is governed by its own privacy policy, available at
        <a href="https://stripe.com/privacy" rel="external nofollow noopener" target="_blank">https://stripe.com/privacy</a>.
      </p>

      <h3>Information from Connected Third-Party Services</h3>
      <p>The Service allows You to connect Your Organization to third-party accounting services, currently Xero. If You
        choose to connect such a service, You will be asked to authorise the connection through that service's own
        login. We receive an access token allowing Us to read and write the accounting records You have authorised us
        to synchronise &mdash; such as contacts, invoices, credit notes and payments &mdash; together with basic details
        of the connected organisation. We do not receive Your password for that service. You may disconnect the
        integration at any time from within the Service, or by revoking Our access from the third party's own
        settings.</p>
      <p>The Service also uses the Google Maps Platform to suggest and validate addresses as You type them. Address
        text You enter into those fields is sent to Google for that purpose. Google's use of information is governed by
        the Google Privacy Policy, available at <a href="https://policies.google.com/privacy"
                                                   rel="external nofollow noopener"
                                                   target="_blank">https://policies.google.com/privacy</a>.</p>

      <h2>Cookies, Local Storage and Similar Technologies</h2>
      <p>We use Cookies, browser local storage and similar technologies to operate the Service and to hold certain
        information. The technologies We use are strictly necessary ones: they keep You signed in, remember Your
        Organization and interface preferences between visits, hold data offline so the Service continues to work on a
        poor connection, and protect the Service against fraudulent or abusive use. We do not use Cookies for
        advertising, and We do not sell information about Your browsing to anyone.</p>
      <p>You can instruct Your browser to refuse Cookies or to clear local storage. If You do so, You may not be able to
        sign in or use some parts of our Service.</p>

      <h1>Use of Your Personal Data</h1>
      <p>The Company may use Personal Data for the following purposes:</p>
      <ul>
        <li>
          <p><strong>To provide and maintain our Service</strong>, including to monitor the usage of our Service.</p>
        </li>
        <li>
          <p><strong>To manage Your Account:</strong> to manage Your registration as a user of the Service. The Personal
            Data You provide can give You access to different functionalities of the Service that are available to You
            as a registered user.</p>
        </li>
        <li>
          <p><strong>For the performance of a contract:</strong> the development, compliance and undertaking of the
            subscription contract for the Service, or of any other contract with Us through the Service.</p>
        </li>
        <li>
          <p><strong>To contact You:</strong> by email, telephone, SMS or other equivalent forms of electronic
            communication regarding updates or informative communications related to the functionalities, products or
            contracted services, including security updates, when necessary or reasonable for their implementation.</p>
        </li>
        <li>
          <p><strong>To process payments</strong> and to administer Your subscription, including sending You invoices,
            receipts and payment reminders.</p>
        </li>
        <li>
          <p><strong>To provide support:</strong> to respond to Your enquiries, investigate faults and resolve
            problems You report to Us.</p>
        </li>
        <li>
          <p><strong>To keep the Service secure:</strong> to detect, prevent and address technical issues, fraud,
            unauthorised access and other abuse of the Service.</p>
        </li>
        <li>
          <p><strong>To improve the Service:</strong> to understand which features are used and how, so We can improve
            reliability and design. Wherever practical We use aggregated or de-identified information for this
            purpose.</p>
        </li>
        <li>
          <p><strong>To comply with the law:</strong> to meet Our legal, taxation, accounting and regulatory
            obligations, and to establish, exercise or defend legal claims.</p>
        </li>
        <li>
          <p><strong>For business transfers:</strong> We may use Your information to evaluate or conduct a merger,
            divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our
            assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which
            Personal Data held by Us about our Service users is among the assets transferred.</p>
        </li>
      </ul>
      <p>We do not sell Your Personal Data, and We do not use the Business Contact Data in Your Organization to market
        to Your customers.</p>

      <h1>Disclosure of Your Personal Data</h1>
      <p>We may share Your personal information in the following situations:</p>
      <ul>
        <li>
          <p><strong>With Service Providers:</strong> We may share Your personal information with Service Providers to
            operate the Service, to process payments, to send email on Our behalf and to monitor the use of our
            Service.</p>
        </li>
        <li>
          <p><strong>Within Your Organization:</strong> information You enter into the Service is visible to other users
            of Your Organization according to the roles and permissions Your administrators have granted them. Your
            administrators can access, export and delete records in Your Organization, including records You
            created.</p>
        </li>
        <li>
          <p><strong>With services You connect:</strong> where You authorise an integration such as Xero, We share the
            records necessary to perform the synchronisation You have asked for.</p>
        </li>
        <li>
          <p><strong>For business transfers:</strong> We may share or transfer Your personal information in connection
            with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a
            portion of Our business to another company.</p>
        </li>
        <li>
          <p><strong>With Affiliates:</strong> We may share Your information with Our affiliates, in which case We will
            require those affiliates to honour this Privacy Policy.</p>
        </li>
        <li>
          <p><strong>With Your consent:</strong> We may disclose Your personal information for any other purpose with
            Your consent.</p>
        </li>
      </ul>

      <h2>Law enforcement</h2>
      <p>Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do so by
        law or in response to valid requests by public authorities (e.g. a court or a government agency).</p>

      <h2>Other legal requirements</h2>
      <p>The Company may disclose Your Personal Data in the good faith belief that such action is necessary to:</p>
      <ul>
        <li>
          <p>Comply with a legal obligation</p>
        </li>
        <li>
          <p>Protect and defend the rights or property of the Company</p>
        </li>
        <li>
          <p>Prevent or investigate possible wrongdoing in connection with the Service</p>
        </li>
        <li>
          <p>Protect the personal safety of Users of the Service or the public</p>
        </li>
        <li>
          <p>Protect against legal liability</p>
        </li>
      </ul>

      <h1>Our Service Providers</h1>
      <p>We rely on the following categories of Service Provider. This list reflects the providers in use at the date of
        this Privacy Policy and may change as the Service develops.</p>
      <ul>
        <li>
          <p><strong>Amazon Web Services (AWS)</strong> &mdash; hosting, databases, file storage, content delivery,
            notifications and outbound email for the Service.</p>
        </li>
        <li>
          <p><strong>Stripe</strong> &mdash; payment processing for subscriptions and for invoice payments taken through
            the Service.</p>
        </li>
        <li>
          <p><strong>Xero</strong> &mdash; accounting synchronisation, where You have connected Your Organization.</p>
        </li>
        <li>
          <p><strong>Google</strong> &mdash; address lookup and validation through the Google Maps Platform.</p>
        </li>
        <li>
          <p><strong>Sentry</strong> &mdash; collection of crash reports and error diagnostics from the Application so
            We can identify and fix faults.</p>
        </li>
        <li>
          <p><strong>Apple and Google</strong> &mdash; distribution of the Application through the App Store and Google
            Play.</p>
        </li>
      </ul>

      <h1>Transfer of Your Personal Data</h1>
      <p>Your information, including Personal Data, is processed at the Company's operating offices and in the places
        where the parties involved in the processing are located. The Company operates the Service from data centres in
        Australia, the United Kingdom and South Africa, and Your information is stored in the region selected for Your
        Organization. Our Service Providers may process information in other countries.</p>
      <p>It means that this information may be transferred to &mdash; and maintained on &mdash; computers located
        outside of Your state, province, country or other governmental jurisdiction where the data protection laws may
        differ from those of Your jurisdiction.</p>
      <p>Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement
        to that transfer.</p>
      <p>The Company will take all steps reasonably necessary to ensure that Your data is treated securely and in
        accordance with this Privacy Policy and no transfer of Your Personal Data will take place to an organization or
        a country unless there are adequate controls in place including the security of Your data and other personal
        information.</p>

      <h1>Security of Your Personal Data</h1>
      <p>The security of Your Personal Data is important to Us. We maintain administrative, technical and physical
        safeguards designed to protect it, including encryption of traffic in transit using TLS, encryption of stored
        data and file attachments at rest, hashing of passwords, authentication using signed, expiring tokens, optional
        biometric unlocking of the Application, role-based access controls within each Organization, isolation of each
        Organization's records, and logging of access and changes.</p>
      <p>But remember that no method of transmission over the Internet, or method of electronic storage is 100% secure.
        While We strive to use commercially acceptable means to protect Your Personal Data, We cannot guarantee its
        absolute security.</p>
      <p>You are responsible for keeping Your password confidential, for using a strong and unique password, and for
        managing which of Your users have access to Your Organization. Please notify Us immediately if You believe Your
        Account has been accessed without Your authorisation.</p>

      <h1>Retention of Your Personal Data</h1>
      <p>The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this
        Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal
        obligations (for example, if We are required to retain Your data to comply with applicable tax and corporations
        laws), resolve disputes, and enforce our legal agreements and policies.</p>
      <p>The Company will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a
        shorter period of time, except when this data is used to strengthen the security or to improve the
        functionality of Our Service, or We are legally obligated to retain this data for longer time periods.</p>
      <p>Records You create within Your Organization are retained for as long as Your Account remains active. Following
        cancellation or termination of Your Account, We will delete or de-identify Your Organization's records within a
        reasonable period, ordinarily within ninety (90) days, unless We are required to retain them for longer.
        Residual copies may persist in encrypted backups for a further period before being overwritten in the ordinary
        course. Please export any records You need before cancelling.</p>

      <h1>Delete Your Personal Data</h1>
      <p>You have the right to delete or request that We assist in deleting the Personal Data that We have collected
        about You.</p>
      <p>Our Service may give You the ability to delete certain information about You from within the Service. You may
        update, amend, or delete Your information at any time by signing in to Your Account and visiting the settings
        section that allows You to manage Your personal information. You may also contact Us to request access to,
        correct, or delete any personal information that You have provided to Us.</p>
      <p>Please note however that We may need to retain certain information when We have a legal obligation or lawful
        basis to do so. Where the information concerned is Business Contact Data held on behalf of an Organization, We
        will refer the request to that Organization and assist it in responding.</p>

      <h1>Your Privacy Rights</h1>
      <h2>Australia</h2>
      <p>We handle personal information in accordance with the <em>Privacy Act 1988</em> (Cth) and the Australian
        Privacy Principles. You may request access to the personal information We hold about You and ask Us to correct
        it if it is inaccurate, out of date, incomplete, irrelevant or misleading. We will respond to Your request
        within a reasonable period, ordinarily within thirty (30) days.</p>
      <p>If You are not satisfied with how We have handled Your personal information or Your request, You may complain
        to Us using the contact details below. We will investigate and respond to You in writing. If You remain
        dissatisfied, You may complain to the Office of the Australian Information Commissioner at
        <a href="https://www.oaic.gov.au" rel="external nofollow noopener" target="_blank">https://www.oaic.gov.au</a>.
      </p>

      <h2>European Economic Area and United Kingdom</h2>
      <p>If You are located in the European Economic Area or the United Kingdom, You have the right to request access
        to, rectification of, or erasure of Your Personal Data; to restrict or object to its processing; to receive Your
        Personal Data in a portable format; and, where processing is based on consent, to withdraw that consent at any
        time without affecting the lawfulness of processing carried out before the withdrawal.</p>
      <p>We process Your Personal Data on the following legal bases: the performance of Our contract with You; Our
        legitimate interests in operating, securing and improving the Service; compliance with a legal obligation; and,
        where applicable, Your consent.</p>
      <p>You also have the right to lodge a complaint with Your local supervisory authority.</p>

      <h2>Other jurisdictions</h2>
      <p>Where Your Organization's data is hosted in South Africa, We handle personal information in a manner consistent
        with the Protection of Personal Information Act, 2013. Wherever You are located, You may exercise the rights
        described above by contacting Us, and We will honour them to the extent the law applicable to You requires.</p>
      <p>We do not charge a fee to handle a request unless the law permits it and the request is manifestly unfounded or
        excessive. We may need to verify Your identity before acting on a request.</p>

      <h1>Children's Privacy</h1>
      <p>Our Service does not address anyone under the age of 18. We do not knowingly collect personally identifiable
        information from anyone under the age of 18. If You are a parent or guardian and You are aware that Your child
        has provided Us with Personal Data, please contact Us. If We become aware that We have collected Personal Data
        from anyone under the age of 18 without verification of parental consent, We take steps to remove that
        information from Our servers.</p>

      <h1>Links to Other Websites</h1>
      <p>Our Service may contain links to other websites that are not operated by Us. If You click on a third party
        link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of
        every site You visit.</p>
      <p>We have no control over and assume no responsibility for the content, privacy policies or practices of any
        third party sites or services.</p>

      <h1>Changes to this Privacy Policy</h1>
      <p>We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new
        Privacy Policy on this page.</p>
      <p>We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective
        and update the &quot;Last updated&quot; date at the top of this Privacy Policy.</p>
      <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are
        effective when they are posted on this page.</p>

      <h1>Contact Us</h1>
      <p>If you have any questions about this Privacy Policy, You can contact us:</p>
      <ul>
        <li>
          <p>By email: info&#64;invflow.com or fabio&#64;loreggian.com</p>
        </li>
        <li>
          <p>By visiting this page on our website: <a href="https://app.invflow.com/contactus"
                                                      rel="external nofollow noopener" target="_blank">https://app.invflow.com/contactus</a>
          </p>
        </li>
        <li>
          <p>By phone number: +61413291005</p>
        </li>
      </ul>

    </mat-card-content>
  </mat-card>
</div>
